import {
  AppleFilled,
  DownloadOutlined,
  WindowsFilled,
} from '@ant-design/icons';
import { Alert, Button, Card, Col, Row, Space, Tag, Typography } from 'antd';
import { AotvLogo } from '../components/AotvLogo';
import { PageHead } from '../components/PageHead';
import {
  SLIDE_CONTROL_DOWNLOADS,
  detectPlatform,
} from '../lib/slideControlDownloads';

const { Title, Text, Paragraph } = Typography;

/**
 * Public install page for the AOTV Slide Control desktop apps.
 *
 * Deliberately unauthenticated: freelance operators and guest venues need to
 * install this on a show laptop without an AOTV login, and the download URLs
 * themselves are already public (the Worker holds the only GitHub
 * credential). The session key, which is the thing that actually grants
 * control of an event, still comes from the team-only Slide licenses page.
 */
export function DownloadsScene() {
  const platform = detectPlatform();

  const windows = (
    <Card
      title={
        <Space>
          <WindowsFilled />
          <span>Windows</span>
          {platform === 'windows' && <Tag color="green">Your platform</Tag>}
        </Space>
      }
      style={{ height: '100%' }}
    >
      <Paragraph type="secondary">
        Windows 10 or 11, 64-bit. Drives Microsoft PowerPoint and streams
        presenter notes back to the speaker. Nothing else to install — the
        download carries its own .NET runtime.
      </Paragraph>
      <Button
        type="primary"
        size="large"
        icon={<DownloadOutlined />}
        href={SLIDE_CONTROL_DOWNLOADS.windows}
        block
      >
        Download for Windows
      </Button>
      <ol style={{ paddingLeft: 20, marginTop: 16, marginBottom: 0 }}>
        <li>
          Unzip the download, then run <Text code>Install.cmd</Text>. It
          installs for the current user only — no admin password needed.
        </li>
        <li>
          Windows SmartScreen may say <Text strong>"Windows protected your
          PC"</Text> the first time. Click <Text strong>More info</Text> →{' '}
          <Text strong>Run anyway</Text>. We don't yet pay for a code-signing
          certificate.
        </li>
        <li>
          Click the AOTV icon near the clock, paste the session key your
          producer gave you, and hit <Text strong>Connect</Text>.
        </li>
        <li>
          Open your deck in PowerPoint. For Google Slides, Canva or a web
          deck, switch <Text strong>Mode</Text> to{' '}
          <Text strong>Generic</Text> before connecting.
        </li>
      </ol>
    </Card>
  );

  const mac = (
    <Card
      title={
        <Space>
          <AppleFilled />
          <span>macOS</span>
          {platform === 'mac' && <Tag color="green">Your platform</Tag>}
        </Space>
      }
      style={{ height: '100%' }}
    >
      <Paragraph type="secondary">
        macOS 13 Ventura or later. Drives Keynote and PowerPoint, and streams
        presenter notes back to the speaker.
      </Paragraph>
      <Button
        type="primary"
        size="large"
        icon={<DownloadOutlined />}
        href={SLIDE_CONTROL_DOWNLOADS.mac}
        block
      >
        Download for macOS
      </Button>
      <ol style={{ paddingLeft: 20, marginTop: 16, marginBottom: 0 }}>
        <li>
          Unzip and drag <Text code>AOTV Slide Control.app</Text> into{' '}
          <Text code>/Applications</Text>.
        </li>
        <li>
          First launch only: Gatekeeper may block it. Right-click the app in
          Finder → <Text strong>Open</Text> → <Text strong>Open</Text>.
        </li>
        <li>
          Click the AOTV emblem in the menu bar, paste the session key, and hit{' '}
          <Text strong>Connect</Text>.
        </li>
        <li>
          Grant <Text strong>Automation</Text> for Keynote and PowerPoint, plus{' '}
          <Text strong>Accessibility</Text>, when prompted. Without these,
          slide advance and notes reading fail silently mid-show.
        </li>
      </ol>
    </Card>
  );

  return (
    <div className="auth-container">
      <PageHead title="Download Slide Control" />
      <Row justify="center">
        <Col xs={23} sm={22} md={20} lg={18} xl={16}>
          <div className="auth-logo">
            <AotvLogo />
          </div>

          <Title level={2} style={{ marginBottom: 4 }}>
            AOTV Slide Control
          </Title>
          <Paragraph type="secondary">
            Install this on the laptop running the slides. It connects to the
            speaker's controls on call.aotv.co.uk and advances the deck for
            them, wherever in the world they are.
          </Paragraph>

          <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
            <Col xs={24} md={12}>
              {platform === 'mac' ? mac : windows}
            </Col>
            <Col xs={24} md={12}>
              {platform === 'mac' ? windows : mac}
            </Col>
          </Row>

          <Alert
            style={{ marginTop: 24 }}
            type="info"
            showIcon
            message="You'll need a session key"
            description="Your AOTV producer generates one per slides machine on the Slide licenses page. The same key can be reused for every speaker sharing that laptop, and it survives reboots — paste it once."
          />

          <Paragraph type="secondary" style={{ marginTop: 24 }}>
            Both apps update themselves in the background, so a laptop set up
            once stays current. Trouble? Email{' '}
            <a href="mailto:info@aotv.co.uk">info@aotv.co.uk</a>.
          </Paragraph>
        </Col>
      </Row>
    </div>
  );
}
